import { createApiRef, DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';

export interface Tenant {
  id: number;
  name: string;
  short_name: string;
  apps: string[];
  profile?: any;
  address?: any;
  billing?: any;
  settings?: any;
  feature_gates?: any;
  branding?: any;
  infrastructure?: any;
  created_at: string;
  updated_at: string;
}

export interface TenantApi {
  getAllTenants(): Promise<Tenant[]>;
  getTenant(id: string): Promise<Tenant>;
  getTenantByShortName(shortName: string): Promise<Tenant>;
  updateTenant(shortName: string, updatedTenant: Partial<Tenant>): Promise<Tenant>;
  deleteTenant(shortName: string): Promise<void>;
}

// Modify the ID to ensure uniqueness and avoid conflicts
export const tenantApiRef = createApiRef<TenantApi>({
  id: 'plugin.tenant-frontend.service',
});

export class TenantClient implements TenantApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  async getAllTenants(): Promise<Tenant[]> {
    const baseUrl = await this.discoveryApi.getBaseUrl('tenants');
    console.log('Base URL:', baseUrl); // Debugging line
    const response = await this.fetchApi.fetch(`${baseUrl}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch tenants: ${response.status} ${response.statusText}`);
    }
    return await response.json();
  }

  async getTenant(id: string): Promise<Tenant> {
    const baseUrl = await this.discoveryApi.getBaseUrl('tenants');
    const response = await this.fetchApi.fetch(`${baseUrl}/${id}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch tenant ${id}: ${response.status} ${response.statusText}`);
    }
    return await response.json();
  }

  async getTenantByShortName(shortName: string): Promise<Tenant> {
    const baseUrl = await this.discoveryApi.getBaseUrl('tenants');
    const response = await this.fetchApi.fetch(`${baseUrl}/${shortName}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch tenant with short name ${shortName}: ${response.status} ${response.statusText}`);
    }
    return await response.json();
  }

  async deleteTenant(shortName: string): Promise<void> {
    const baseUrl = await this.discoveryApi.getBaseUrl('tenants');
    const response = await this.fetchApi.fetch(`${baseUrl}/${shortName}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      throw new Error(`Failed to delete tenant ${shortName}: ${response.status} ${response.statusText}`);
    }
  }

  async updateTenant(shortName: string, updatedTenant: Partial<Tenant>): Promise<Tenant> {
    const baseUrl = await this.discoveryApi.getBaseUrl('tenants');
    const response = await this.fetchApi.fetch(`${baseUrl}/${shortName}`, {
      method: 'PUT',
      body: JSON.stringify(updatedTenant),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (!response.ok) {
      // Surface the backend's message (e.g. which app names are unknown)
      // rather than only the status line.
      const detail = await response.json().then(b => b?.error).catch(() => undefined);
      throw new Error(`Failed to update tenant ${shortName}: ${detail ?? `${response.status} ${response.statusText}`}`);
    }
    return await response.json();
  }
}
